/**
 * Forms and intake (WP3-FORMS, BUILD-PLAN M22). Staff pages: the review
 * queue, sent packets and the form library (`/forms`), and the builder
 * (`/forms/templates/$templateId`). The patient's link is `/f/$token`; it
 * renders without the app shell once the shell treats it as a bare route
 * (docs/requests/wp3-forms-bare-patient-route.md). The paperwork wizard is
 * `/i/$token` (the same packet, guided step by step). An office's website
 * "New patient forms" button opens `/new-patient/$slug` (no sign-in). A
 * shared form is `/f/$office/$form`, and inside an office website's iframe
 * `/embed/f/$office/$form` (then `/embed/f/$office/$form/$token`). Links and
 * embed code live in Settings > Patient forms & links
 * (`/settings/patient-forms`).
 *
 * Other modules open a review with `/forms?review=<packetId>`.
 */
import { defineManifest } from '@velora/contracts';
import { FORMS_PERMISSIONS } from '@velora/contracts/forms';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'forms',
    title: 'Forms and intake',
    version: '0.1.0',
    owner: 'WP3-FORMS',
    entitlementKey: null,
    permissions: [...new Set(Object.values(FORMS_PERMISSIONS))],
    routes: [
      {
        id: 'hub',
        path: '/forms',
        title: 'Forms',
        officeScope: 'location-or-all',
        permission: FORMS_PERMISSIONS.view,
        readsPhi: true,
      },
      {
        id: 'builder',
        path: '/forms/templates/$templateId',
        title: 'Form builder',
        officeScope: 'practice',
        permission: FORMS_PERMISSIONS.view,
      },
      { id: 'patient-link', path: '/f/$token', title: 'Your forms', officeScope: 'practice' },
      { id: 'intake-link', path: '/i/$token', title: 'Your paperwork', officeScope: 'practice' },
      {
        id: 'web-new-patient',
        path: '/new-patient/$slug',
        title: 'New patient forms',
        officeScope: 'practice',
      },
      // A shared form link and its website embed (contracts/forms-share.ts).
      {
        id: 'share-link',
        path: '/f/$office/$form',
        title: 'Patient form',
        officeScope: 'practice',
      },
      {
        id: 'embed-share',
        path: '/embed/f/$office/$form',
        title: 'Patient form',
        officeScope: 'practice',
      },
      {
        id: 'embed-fill',
        path: '/embed/f/$office/$form/$token',
        title: 'Your forms',
        officeScope: 'practice',
      },
      {
        // Settings > Patient forms & links (SettingsFrame lists it under Connections).
        id: 'links',
        path: '/settings/patient-forms',
        title: 'Patient forms & links',
        officeScope: 'location-or-all',
        permission: FORMS_PERMISSIONS.manage,
      },
    ],
    nav: [
      {
        label: 'Forms',
        group: 'patients',
        routeId: 'hub',
        icon: 'clipboard-list',
        order: 40,
        permission: FORMS_PERMISSIONS.view,
      },
    ],
    commands: [
      {
        id: 'forms.open',
        title: 'Go to Forms',
        keywords: ['intake', 'review', 'medical history', 'consent', 'registration'],
        routeId: 'hub',
        permission: FORMS_PERMISSIONS.view,
      },
      {
        id: 'forms.links',
        title: 'Patient forms & links',
        keywords: ['embed', 'website', 'iframe', 'form link', 'qr code', 'portal link', 'sign in'],
        routeId: 'links',
        permission: FORMS_PERMISSIONS.manage,
      },
    ],
    shortcuts: [{ keys: 'g q', description: 'Go to Forms', routeId: 'hub' }],
  }),
  pages: {
    hub: () => import('./FormsPage.js'),
    builder: () => import('./BuilderPage.js'),
    'patient-link': () => import('./PatientFormPage.js'),
    'intake-link': () => import('./IntakePage.js'),
    'web-new-patient': () => import('./NewPatientWebPage.js'),
    'share-link': () => import('./ShareFormPage.js'),
    'embed-share': () => import('./ShareFormPage.js'),
    'embed-fill': () => import('./PatientFormPage.js'),
    links: () => import('./LinksPage.js'),
  },
});
